import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/msalConfig';
import {
  fetchEmployees,
  createEmployee,
  updateEmployee,
  importEmployeesCsv,
  syncEmployeesFromEntra,
  deleteEmployees,
  getEmployeeTemplateUrl,
  type Employee,
  type CommunicationPreference,
} from '../../api/employeeApi';

const PREFS: CommunicationPreference[] = ['Email', 'Teams', 'Sms'];

function prefLabel(p: CommunicationPreference) {
  return p === 'Sms' ? 'SMS' : p;
}

function EmployeeRow({
  employee,
  onSave,
  selected,
  onToggle,
}: {
  employee: Employee;
  onSave: (updated: Employee) => void;
  selected: boolean;
  onToggle: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(employee);

  const handleSave = () => { onSave(draft); setEditing(false); };

  if (!editing) {
    return (
      <tr style={{ borderBottom: '1px solid var(--color-border)', background: selected ? 'var(--color-info-bg)' : undefined }}>
        <td style={{ ...td, textAlign: 'center' }}>
          <input type="checkbox" checked={selected} onChange={() => onToggle(employee.id)} aria-label={`Select ${employee.name}`} />
        </td>
        <td style={td}>{employee.name}</td>
        <td style={td}>{employee.email}</td>
        <td style={td}>{employee.teamLabel}</td>
        <td style={td}>{employee.department ?? '—'}</td>
        <td style={td}>{employee.manager ?? '—'}</td>
        <td style={td}>{prefLabel(employee.communicationPreference)}</td>
        <td style={td}>{employee.phoneNumber ?? '—'}</td>
        <td style={td}>
          <button className="btn btn-ghost" style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem' }}
            onClick={() => setEditing(true)}>Edit</button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-info-bg)' }}>
      <td style={td}></td>
      <td style={td}><input style={input} value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} /></td>
      <td style={td}><input style={input} value={draft.email} onChange={e => setDraft(d => ({ ...d, email: e.target.value }))} /></td>
      <td style={td}><input style={input} value={draft.teamLabel} onChange={e => setDraft(d => ({ ...d, teamLabel: e.target.value }))} /></td>
      <td style={td}><input style={input} value={draft.department ?? ''} placeholder="—" onChange={e => setDraft(d => ({ ...d, department: e.target.value || null }))} /></td>
      <td style={td}><input style={input} value={draft.manager ?? ''} placeholder="—" onChange={e => setDraft(d => ({ ...d, manager: e.target.value || null }))} /></td>
      <td style={td}>
        <select style={input} value={draft.communicationPreference}
          onChange={e => setDraft(d => ({ ...d, communicationPreference: e.target.value as CommunicationPreference }))}>
          {PREFS.map(p => <option key={p} value={p}>{prefLabel(p)}</option>)}
        </select>
      </td>
      <td style={td}><input style={input} value={draft.phoneNumber ?? ''} placeholder="+1…" onChange={e => setDraft(d => ({ ...d, phoneNumber: e.target.value || null }))} /></td>
      <td style={td}>
        <button className="btn btn-primary" style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem', marginRight: 6 }} onClick={handleSave}>Save</button>
        <button className="btn btn-ghost" style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem' }} onClick={() => setEditing(false)}>Cancel</button>
      </td>
    </tr>
  );
}

type NewEmpDraft = {
  name: string; email: string; personalEmail: string;
  teamLabel: string; department: string; manager: string;
  communicationPreference: CommunicationPreference; phoneNumber: string;
};

const blankDraft = (): NewEmpDraft => ({
  name: '', email: '', personalEmail: '', teamLabel: '',
  department: '', manager: '', communicationPreference: 'Email', phoneNumber: '',
});

export function Employees() {
  const { instance, accounts } = useMsal();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [newEmp, setNewEmp] = useState<NewEmpDraft>(blankDraft());
  const fileRef = useRef<HTMLInputElement>(null);

  const getToken = async () => {
    const r = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return r.accessToken;
  };

  useEffect(() => {
    getToken().then(t => fetchEmployees(t))
      .then(setEmployees)
      .catch(() => setError('Could not load employees.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (updated: Employee) => {
    const token = await getToken();
    const saved = await updateEmployee(token, updated.id, updated);
    setEmployees(prev => prev.map(e => e.id === saved.id ? saved : e));
  };

  const handleCreate = async () => {
    const token = await getToken();
    const created = await createEmployee(token, {
      name: newEmp.name,
      email: newEmp.email,
      personalEmail: newEmp.personalEmail || undefined,
      teamLabel: newEmp.teamLabel,
      department: newEmp.department || undefined,
      manager: newEmp.manager || undefined,
      communicationPreference: newEmp.communicationPreference,
      phoneNumber: newEmp.phoneNumber || undefined,
    });
    setEmployees(prev => [...prev, created]);
    setNewEmp(blankDraft());
    setShowAdd(false);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const token = await getToken();
    const result = await importEmployeesCsv(token, file);
    const warnPart = result.warnings?.length ? ` ${result.warnings.length} warning(s).` : '';
    setImportMsg(`Created ${result.created}, updated ${result.updated} employee(s).${warnPart}`);
    setEmployees(await fetchEmployees(token));
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleEntraSync = async () => {
    setSyncing(true);
    setImportMsg(null);
    try {
      const token = await getToken();
      const result = await syncEmployeesFromEntra(token);
      const warnPart = result.warnings?.length ? ` ${result.warnings.length} warning(s).` : '';
      setImportMsg(`Entra sync: ${result.created} added, ${result.updated} updated, ${result.deactivated} deactivated.${warnPart}`);
      setEmployees(await fetchEmployees(token));
    } catch {
      setError('Entra sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const toggleSelect = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allSelected = employees.length > 0 && selected.size === employees.length;
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(employees.map(e => e.id)));

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} employee(s)? This cannot be undone.`)) return;
    setDeleting(true);
    setImportMsg(null);
    try {
      const token = await getToken();
      const result = await deleteEmployees(token, [...selected]);
      setImportMsg(`Deleted ${result.deleted} employee(s).`);
      setSelected(new Set());
      setEmployees(await fetchEmployees(token));
    } catch {
      setError('Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>;
  if (error) return <p style={{ color: 'var(--color-error)' }}>{error}</p>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Employees ({employees.length})</h2>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {selected.size > 0 && (
            <button className="btn btn-ghost"
              style={{ minHeight: 40, padding: '0 1rem', fontSize: '0.9rem', color: 'var(--color-error)', borderColor: 'var(--color-error)' }}
              onClick={handleDeleteSelected} disabled={deleting}>
              {deleting ? 'Deleting…' : `Delete selected (${selected.size})`}
            </button>
          )}
          <a
            href={getEmployeeTemplateUrl()}
            download
            className="btn btn-ghost"
            style={{ minHeight: 40, padding: '0 1rem', fontSize: '0.9rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            Download template
          </a>
          <label className="btn btn-ghost" style={{ minHeight: 40, padding: '0 1rem', cursor: 'pointer', fontSize: '0.9rem' }}>
            Import CSV
            <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImport} />
          </label>
          <button className="btn btn-ghost" style={{ minHeight: 40, padding: '0 1rem', fontSize: '0.9rem' }} onClick={handleEntraSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync from Entra'}
          </button>
          <button className="btn btn-primary" style={{ minHeight: 40, padding: '0 1rem', fontSize: '0.9rem' }} onClick={() => setShowAdd(s => !s)}>
            + Add employee
          </button>
        </div>
      </div>

      {importMsg && (
        <p style={{ background: 'var(--color-info-bg)', border: '1px solid var(--color-info-border)', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {importMsg}
        </p>
      )}

      {showAdd && (
        <div style={{ background: 'var(--color-info-bg)', border: '1px solid var(--color-info-border)', borderRadius: 8, padding: '1rem', marginBottom: '1.25rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem' }}>
          {([
            ['name', 'Name'], ['email', 'Work email'], ['personalEmail', 'Personal email'],
            ['teamLabel', 'Team'], ['department', 'Department'], ['manager', 'Manager'],
          ] as [keyof NewEmpDraft, string][]).map(([field, label]) => (
            <input key={field} style={input} placeholder={label}
              value={newEmp[field] as string}
              onChange={e => setNewEmp(d => ({ ...d, [field]: e.target.value }))} />
          ))}
          <select style={input} value={newEmp.communicationPreference}
            onChange={e => setNewEmp(d => ({ ...d, communicationPreference: e.target.value as CommunicationPreference }))}>
            {PREFS.map(p => <option key={p} value={p}>{prefLabel(p)}</option>)}
          </select>
          <input style={input} placeholder="Phone (+1…)" value={newEmp.phoneNumber}
            onChange={e => setNewEmp(d => ({ ...d, phoneNumber: e.target.value }))} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary" style={{ minHeight: 40, flex: 1 }} onClick={handleCreate}>Add</button>
            <button className="btn btn-ghost" style={{ minHeight: 40 }} onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
        CSV: employee_name, work_email, personal_email, mobile_phone, team, department, manager, communication_preference (Email/Teams/Sms), entra_object_id
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
              <th style={{ ...td, width: 36, textAlign: 'center', paddingBottom: '0.5rem' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="Select all" />
              </th>
              {['Name', 'Work Email', 'Team', 'Dept', 'Manager', 'Channel', 'Phone', ''].map(h => (
                <th key={h} style={{ ...td, fontWeight: 600, textAlign: 'left', paddingBottom: '0.5rem' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map(e => (
              <EmployeeRow key={e.id} employee={e} onSave={handleSave}
                selected={selected.has(e.id)} onToggle={toggleSelect} />
            ))}
          </tbody>
        </table>
        {employees.length === 0 && (
          <p style={{ color: 'var(--color-text-muted)', padding: '1.5rem 0', textAlign: 'center' }}>
            No employees yet. Add one or import a CSV.
          </p>
        )}
      </div>
    </div>
  );
}

const td: React.CSSProperties = { padding: '0.55rem 0.75rem' };
const input: React.CSSProperties = {
  width: '100%', padding: '0.4rem 0.6rem', fontSize: '0.9rem',
  border: '1px solid var(--color-border)', borderRadius: 4,
  fontFamily: 'inherit', background: 'var(--color-surface)',
};
